// frontend/src/pages/MeuPlanoPage/MeuPlanoPage.jsx
// Gerenciar assinatura do Saldo Ativo: alterar plano, trocar cartão, cancelar

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Card,
  Button,
  Space,
  Spin,
  Alert,
  Modal,
  App,
  Descriptions,
  Radio,
  Form,
  Input,
  Table,
} from 'antd';
import {
  CreditCardOutlined,
  StopOutlined,
  SyncOutlined,
  ShoppingOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import {
  getMinhaAssinatura,
  getPlanos,
  previewAlterarPlano,
  alterarPlano,
  getFormaPagamento,
  alterarFormaPagamento,
  tokenizarCartao,
  atualizarCartao,
  cancelarAssinatura,
  reativarAssinatura,
  cancelarAlteracaoPendente,
  getFaturas,
} from '../../services/assinaturaService';
import { getErrorMessage } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  luhnCheck,
  getCardBrand,
  getAcceptedLengths,
  getCvvLength,
  getCardBrandLabel,
  isExpiryFuture,
  isValidCpfOrCnpj,
  formatCardNumberDisplay,
} from '../../utils/cardValidation';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatCurrency = (v) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const MeuPlanoPage = () => {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { refreshUser } = useAuth();
  const { concedido: acessoCompartilhado } = useWorkspace() || {};
  const [assinatura, setAssinatura] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [modalCartaoOpen, setModalCartaoOpen] = useState(false);
  const [modalFormaPagamentoOpen, setModalFormaPagamentoOpen] = useState(false);
  const [formaPagamentoInfo, setFormaPagamentoInfo] = useState(null);
  const [formaPagamentoSelecionada, setFormaPagamentoSelecionada] = useState(null);
  const [formCartao] = Form.useForm();

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sub, lista] = await Promise.all([
        getMinhaAssinatura().catch(() => null),
        getPlanos(),
      ]);
      setAssinatura(sub);
      setPlanos(lista);
      if (sub?.asaas_subscription_id) {
        getFaturas()
          .then((d) => setFaturas(d.faturas || []))
          .catch(() => setFaturas([]));
      } else {
        setFaturas([]);
      }
      await refreshUser();
      if (!sub) {
        navigate('/checkout', { replace: true });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível carregar.'));
    } finally {
      setLoading(false);
    }
  }, [navigate, refreshUser]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const [modalPlano, setModalPlano] = useState(false);
  const [modalReativar, setModalReativar] = useState(false);
  const [reativarPlanoId, setReativarPlanoId] = useState(null);
  const [faturas, setFaturas] = useState([]);

  const handleAlterarPlano = () => setModalPlano(true);

  const handleCancelarAlteracao = async () => {
    try {
      setActionLoading('cancelar-alt');
      await cancelarAlteracaoPendente();
      message.success('Alteração pendente cancelada.');
      carregar();
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível cancelar a alteração.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSelecionarPlano = async (planoId) => {
    if (planoId === assinatura?.plano?.id) {
      message.info('Este já é o seu plano atual.');
      return;
    }
    if (planoId === assinatura?.plano_pendente?.id) {
      message.info('Esta alteração já está agendada para a próxima renovação.');
      return;
    }
    try {
      setActionLoading('plano');
      const preview = await previewAlterarPlano(planoId);
      const atual = preview.plano_atual;
      const novo = preview.plano_novo;
      const proximaStr = preview.proxima_cobranca
        ? formatarData(preview.proxima_cobranca)
        : null;
      const textoProxima = proximaStr
        ? ` A próxima cobrança será no dia ${proximaStr}.`
        : '';
      const isUpgrade = preview.tipo_mudanca === 'upgrade';
      const isDowngrade = preview.tipo_mudanca === 'downgrade';
      const valorACobrar = preview.valor_a_cobrar ?? preview.valor_pro_rata;
      const valorCalculado = preview.valor_pro_rata_calculado;
      const cobrancaMinima = preview.cobranca_minima_aplicada;
      modal.confirm({
        title: 'Confirmar alteração de plano',
        icon: <ExclamationCircleFilled />,
        content: (
          <div>
            <p>
              Tem certeza que deseja alterar o plano de <strong>{atual.nome}</strong> no valor{' '}
              <strong>{formatCurrency(atual.valor)}</strong> para o plano <strong>{novo.nome}</strong>{' '}
              no valor <strong>{formatCurrency(novo.valor)}</strong>?
            </p>
            {isUpgrade && valorACobrar >= 0.01 && (
              <p>
                Será cobrada agora a diferença proporcional de{' '}
                <strong>{formatCurrency(valorACobrar)}</strong> pelos{' '}
                {preview.dias_restantes} dia(s) restantes do ciclo atual.
              </p>
            )}
            {isUpgrade && cobrancaMinima && preview.mensagem_minimo && (
              <p style={{ color: 'var(--ant-color-warning)' }}>{preview.mensagem_minimo}</p>
            )}
            {isUpgrade && cobrancaMinima && valorCalculado != null && (
              <p>
                Valor proporcional calculado: <strong>{formatCurrency(valorCalculado)}</strong>.
              </p>
            )}
            {isDowngrade && (
              <p>
                O novo plano passará a valer na próxima renovação. Você mantém acesso aos módulos
                atuais até lá.
              </p>
            )}
            {!isDowngrade && textoProxima && <p>{textoProxima.trim()}</p>}
          </div>
        ),
        okText: isUpgrade && valorACobrar >= 0.01 ? 'Sim, continuar para pagamento' : 'Sim, alterar',
        cancelText: 'Não',
        onOk: async () => {
          try {
            setActionLoading('plano');
            const resultado = await alterarPlano(planoId);
            if (resultado.requer_pagamento && resultado.checkout_url) {
              message.info(resultado.mensagem || 'Redirecionando para pagamento...');
              window.location.href = resultado.checkout_url;
              return;
            }
            message.success(resultado.mensagem || 'Plano alterado com sucesso.');
            setModalPlano(false);
            carregar();
          } catch (err) {
            message.error(getErrorMessage(err, 'Não foi possível alterar o plano.'));
          } finally {
            setActionLoading(null);
          }
        },
        onCancel: () => setActionLoading(null),
      });
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível carregar os dados.'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatarData = (str) => {
    if (!str) return null;
    try {
      const s = String(str).trim();
      const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, y, m, d] = match;
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        return isNaN(date.getTime()) ? null : date.toLocaleDateString('pt-BR');
      }
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
    } catch {
      return null;
    }
  };

  const handleTrocarCartao = async () => {
    try {
      setActionLoading('cartao');
      const info = await getFormaPagamento();
      setFormaPagamentoInfo(info);
      const atual = info.forma_pagamento;
      const normalizada =
        atual === 'CREDIT_CARD' || atual === 'DEBIT_CARD'
          ? 'CREDIT_CARD'
          : 'PIX';
      setFormaPagamentoSelecionada(normalizada);
      setModalFormaPagamentoOpen(true);
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível carregar as formas de pagamento.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmarFormaPagamento = async () => {
    if (!formaPagamentoSelecionada) {
      message.warning('Selecione uma forma de pagamento.');
      return;
    }
    if (formaPagamentoSelecionada === 'CREDIT_CARD') {
      setModalFormaPagamentoOpen(false);
      setModalCartaoOpen(true);
      return;
    }
    const atual = formaPagamentoInfo?.forma_pagamento;
    const atualNorm =
      atual === 'CREDIT_CARD' || atual === 'DEBIT_CARD' ? 'CREDIT_CARD' : atual;
    if (formaPagamentoSelecionada === atualNorm) {
      message.info('Esta já é a sua forma de pagamento atual.');
      return;
    }
    try {
      setActionLoading('forma-pagamento');
      const data = await alterarFormaPagamento(formaPagamentoSelecionada);
      message.success(data.mensagem || 'Forma de pagamento alterada.');
      if (data.invoice_url) {
        window.open(data.invoice_url, '_blank');
      }
      setModalFormaPagamentoOpen(false);
      carregar();
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível alterar a forma de pagamento.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitAtualizarCartao = async () => {
    try {
      const values = await formCartao.validateFields();
      const expiryMonth = String(values.expiryMonth).padStart(2, '0');
      const expiryYear = String(values.expiryYear).length === 2 ? `20${values.expiryYear}` : values.expiryYear;
      const creditCard = {
        holderName: values.holderName,
        number: String(values.number).replace(/\s/g, ''),
        expiryMonth,
        expiryYear,
        ccv: String(values.ccv),
      };
      const creditCardHolderInfo = {
        name: values.name,
        email: values.email,
        cpfCnpj: String(values.cpfCnpj).replace(/\D/g, ''),
        postalCode: values.postalCode ? String(values.postalCode).replace(/\D/g, '') : '',
        addressNumber: values.addressNumber || '',
        phone: values.phone ? String(values.phone).replace(/\D/g, '') : '',
      };
      setActionLoading('cartao');
      const { creditCardToken } = await tokenizarCartao({ creditCard, creditCardHolderInfo });
      await atualizarCartao({ creditCardToken });
      message.success('Cartão atualizado com sucesso.');
      setModalCartaoOpen(false);
      setModalFormaPagamentoOpen(false);
      formCartao.resetFields();
      carregar();
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível atualizar o cartão.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReativarConfirm = async () => {
    try {
      setActionLoading('reativar');
      const response = await reativarAssinatura(reativarPlanoId || undefined);
      setModalReativar(false);
      setReativarPlanoId(null);
      if (response?.checkout_url) {
        message.info('Conclua o pagamento no Asaas. A primeira cobrança será na data de renovação.');
        window.location.href = response.checkout_url;
        return;
      }
      message.success(response?.detalhe || 'Assinatura reativada. A próxima cobrança ocorrerá na data de renovação.');
      carregar();
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível reativar.'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelar = () => {
    const acessoAte = assinatura?.proxima_cobranca ? formatarData(assinatura.proxima_cobranca) : null;
    modal.confirm({
      title: 'Cancelar assinatura',
      icon: <ExclamationCircleFilled />,
      content: (
        <div>
          <p>
            {acessoAte
              ? `Você manterá acesso até ${acessoAte} (fim do período já pago). Após essa data, o acesso será encerrado.`
              : 'Você perderá o acesso ao Saldo Ativo ao final do período já pago.'}
          </p>
          <p>Tem certeza que deseja cancelar?</p>
        </div>
      ),
      okText: 'Sim, cancelar',
      okType: 'danger',
      cancelText: 'Não',
      onOk: async () => {
        try {
          setActionLoading('cancelar');
          const res = await cancelarAssinatura();
          const ate = res.acesso_ate ? formatarData(res.acesso_ate) : null;
          message.success(ate ? `Assinatura cancelada. Você mantém acesso até ${ate}.` : 'Assinatura cancelada.');
          carregar();
        } catch (err) {
          message.error(getErrorMessage(err, 'Não foi possível cancelar.'));
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  if (loading) {
    return (
      <Content style={{ padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Spin size="large" tip="Carregando..." />
      </Content>
    );
  }

  if (error) {
    return (
      <Content style={{ padding: '2rem' }}>
        <Alert message="Erro" description={error} type="error" showIcon />
      </Content>
    );
  }

  if (!assinatura) {
    return (
      <Content style={{ padding: '2rem' }}>
        <Title level={2}>Meu Plano</Title>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text>Você ainda não possui uma assinatura ativa do Saldo Ativo.</Text>
            <Button type="primary" icon={<ShoppingOutlined />} onClick={() => navigate('/checkout')}>
              Ver planos e assinar
            </Button>
          </Space>
        </Card>
      </Content>
    );
  }

  const plano = assinatura.plano || {};
  const upgradePendente = assinatura.upgrade_pendente;
  const planoPendente = assinatura.plano_pendente || assinatura.alteracao_agendada?.plano || null;
  const vigenciaPendente = assinatura.plano_pendente_vigencia || assinatura.alteracao_agendada?.vigencia || null;
  const cicloLabel = { mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[plano.ciclo] || plano.ciclo;
  const tipoLabel = { completo: 'Completo', milhas: 'Milhas', financeiro: 'Financeiro' }[plano.tipo] || plano.tipo;
  const cicloPendenteLabel = planoPendente
    ? { mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[planoPendente.ciclo] || planoPendente.ciclo
    : null;
  const tipoPendenteLabel = planoPendente
    ? { completo: 'Completo', milhas: 'Milhas', financeiro: 'Financeiro' }[planoPendente.tipo] || planoPendente.tipo
    : null;
  const valorProximaCobranca = assinatura.proxima_cobranca_valor ?? planoPendente?.valor ?? plano.valor;

  return (
    <Content style={{ padding: '2rem' }}>
      <Title level={2}>Meu Plano</Title>
      {assinatura.inadimplente && assinatura.periodo_graca_ate && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cobrança em atraso"
          description={
            <>
              Atualize o pagamento até <strong>{formatarData(assinatura.periodo_graca_ate)}</strong> para manter o
              acesso. Use &quot;Alterar forma de pagamento&quot; para trocar para PIX ou cartão.
            </>
          }
        />
      )}
      {assinatura.tem_cobranca_pendente && assinatura.invoice_url_pendente && !upgradePendente && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Mensalidade aguardando pagamento"
          description={
            <>
              {assinatura.cobranca_pendente_forma_label && (
                <>
                  Forma: <strong>{assinatura.cobranca_pendente_forma_label}</strong>
                  {assinatura.cobranca_pendente_vencimento && (
                    <>
                      {' '}
                      — vencimento em <strong>{formatarData(assinatura.cobranca_pendente_vencimento)}</strong>
                    </>
                  )}
                  {assinatura.cobranca_pendente_valor != null && (
                    <> ({formatCurrency(assinatura.cobranca_pendente_valor)})</>
                  )}
                </>
              )}
            </>
          }
          action={
            <Button
              type="primary"
              size="small"
              onClick={() => window.open(assinatura.invoice_url_pendente, '_blank', 'noopener,noreferrer')}
            >
              Pagar mensalidade
            </Button>
          }
        />
      )}
      {upgradePendente && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Upgrade aguardando pagamento"
          description={
            <>
              Conclua o pagamento de <strong>{formatCurrency(upgradePendente.valor)}</strong> para ativar o plano{' '}
              <strong>
                {({ completo: 'Completo', milhas: 'Milhas', financeiro: 'Financeiro' }[
                  upgradePendente.plano_novo?.tipo
                ] || upgradePendente.plano_novo?.tipo)}{' '}
                -{' '}
                {({ mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[
                  upgradePendente.plano_novo?.ciclo
                ] || upgradePendente.plano_novo?.ciclo)}
              </strong>
              .
            </>
          }
          action={
            <Space>
              {upgradePendente.checkout_url && (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    window.location.href = upgradePendente.checkout_url;
                  }}
                >
                  Continuar pagamento
                </Button>
              )}
              <Button size="small" loading={actionLoading === 'cancelar-alt'} onClick={handleCancelarAlteracao}>
                Cancelar alteração
              </Button>
            </Space>
          }
        />
      )}
      {planoPendente && vigenciaPendente && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Alteração de plano agendada"
          description={
            <>
              A partir de <strong>{formatarData(vigenciaPendente)}</strong>, seu plano passará para{' '}
              <strong>
                {tipoPendenteLabel} - {cicloPendenteLabel}
              </strong>{' '}
              ({formatCurrency(planoPendente.valor)}). Até lá, você mantém acesso ao plano{' '}
              <strong>
                {tipoLabel} - {cicloLabel}
              </strong>.
            </>
          }
          action={
            <Button size="small" loading={actionLoading === 'cancelar-alt'} onClick={handleCancelarAlteracao}>
              Manter plano atual
            </Button>
          }
        />
      )}
      <Card
        title={`${tipoLabel} - ${cicloLabel}`}
        extra={
          assinatura.cancelada_acesso_ate ? (
            <Text type="warning">Cancelada – acesso até {formatarData(assinatura.proxima_cobranca || assinatura.data_fim)}</Text>
          ) : assinatura.ativa ? (
            <Text type="success">Ativo</Text>
          ) : null
        }
      >
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Valor do plano">{formatCurrency(plano.valor)}</Descriptions.Item>
          {acessoCompartilhado?.status === 'ativo' && (
            <Descriptions.Item label="Acesso compartilhado">
              + {formatCurrency(5)}/mês ({acessoCompartilhado.convidado?.nome || acessoCompartilhado.convite_email})
            </Descriptions.Item>
          )}
          {planoPendente && vigenciaPendente && (
            <Descriptions.Item label="Próximo plano (agendado)">
              {tipoPendenteLabel} - {cicloPendenteLabel} ({formatCurrency(planoPendente.valor)})
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Ciclo">Cobrança {cicloLabel.toLowerCase()}</Descriptions.Item>
          {assinatura.forma_pagamento_label && (
            <Descriptions.Item label="Forma de pagamento">
              {assinatura.forma_pagamento_label}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Início">
            {assinatura.data_inicio
              ? new Date(assinatura.data_inicio).toLocaleDateString('pt-BR')
              : '-'}
          </Descriptions.Item>
          {assinatura.proxima_cobranca && (
            <Descriptions.Item label={assinatura.cancelada_acesso_ate ? 'Acesso até' : 'Próxima cobrança'}>
              {formatarData(assinatura.proxima_cobranca)}
              {!assinatura.cancelada_acesso_ate && (
                <> — {formatCurrency(valorProximaCobranca)}</>
              )}
            </Descriptions.Item>
          )}
        </Descriptions>
        {assinatura.cancelada_acesso_ate && (
          <Space style={{ marginTop: 24 }} wrap>
            {assinatura.asaas_subscription_id ? (
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={() => {
                  setReativarPlanoId(assinatura?.plano?.id ?? null);
                  setModalReativar(true);
                }}
                loading={actionLoading === 'reativar'}
              >
                Reativar assinatura
              </Button>
            ) : (
              <Button
                icon={<ShoppingOutlined />}
                onClick={() => navigate(assinatura?.plano?.id ? `/checkout?plano=${assinatura.plano.id}` : '/checkout')}
              >
                Contratar novamente
              </Button>
            )}
          </Space>
        )}
        <Modal
          title="Reativar assinatura"
          open={modalReativar}
          onCancel={() => setModalReativar(false)}
          onOk={handleReativarConfirm}
          okText="Reativar"
          confirmLoading={actionLoading === 'reativar'}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Reative sua assinatura sem cobrança imediata. A próxima cobrança ocorrerá na data de renovação.
          </Text>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Plano</Text>
          <Radio.Group
            value={reativarPlanoId}
            onChange={(e) => setReativarPlanoId(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical">
              {planos.map((p) => (
                <Radio key={p.id} value={p.id}>
                  {({ completo: 'Completo', milhas: 'Milhas', financeiro: 'Financeiro' }[p.tipo] || p.tipo)} -{' '}
                  {({ mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[p.ciclo] || p.ciclo)} — {formatCurrency(p.valor)}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Modal>
        {assinatura.asaas_subscription_id && !assinatura.cancelada_acesso_ate && (
          <>
            <Space style={{ marginTop: 24 }} wrap>
              <Button
                icon={<SyncOutlined />}
                onClick={handleAlterarPlano}
                loading={actionLoading === 'plano'}
              >
                Alterar plano
              </Button>
            <Button
              icon={<CreditCardOutlined />}
              onClick={handleTrocarCartao}
              loading={actionLoading === 'cartao'}
            >
              Alterar forma de pagamento
            </Button>
            <Button
              danger
              icon={<StopOutlined />}
              onClick={handleCancelar}
              loading={actionLoading === 'cancelar'}
            >
              Cancelar assinatura
            </Button>
          </Space>
            <Modal
              title="Alterar plano"
              open={modalPlano}
              onCancel={() => setModalPlano(false)}
              footer={null}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {planos
                  .filter((p) => p.id !== assinatura?.plano?.id)
                  .map((p) => (
                    <Card
                      key={p.id}
                      size="small"
                      hoverable
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelecionarPlano(p.id)}
                    >
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text strong>
                          {({ completo: 'Completo', milhas: 'Milhas', financeiro: 'Financeiro' }[p.tipo] || p.tipo)} -{' '}
                          {({ mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[p.ciclo] || p.ciclo)}
                        </Text>
                        <Text>{formatCurrency(p.valor)}</Text>
                      </Space>
                    </Card>
                  ))}
              </Space>
            </Modal>
            <Modal
              title="Alterar forma de pagamento"
              open={modalFormaPagamentoOpen}
              onCancel={() => setModalFormaPagamentoOpen(false)}
              onOk={handleConfirmarFormaPagamento}
              okText={
                formaPagamentoSelecionada === 'CREDIT_CARD'
                  ? 'Continuar para atualizar cartão'
                  : 'Confirmar'
              }
              confirmLoading={actionLoading === 'forma-pagamento'}
              destroyOnClose
            >
              {formaPagamentoInfo && (
                <>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Forma atual: <strong>{formaPagamentoInfo.forma_pagamento_label}</strong>
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Ao trocar para PIX, a cobrança pendente (se existir) será regenerada com a nova forma.
                    Se escolher cartão de crédito, você poderá informar um novo cartão (vencido, perdido, etc.).
                  </Text>
                  <Radio.Group
                    value={formaPagamentoSelecionada}
                    onChange={(e) => setFormaPagamentoSelecionada(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <Space direction="vertical">
                      <Radio value="PIX">PIX</Radio>
                      <Radio value="CREDIT_CARD">Cartão de crédito</Radio>
                    </Space>
                  </Radio.Group>
                  {formaPagamentoInfo.invoice_url_pendente && (
                    <Button
                      type="link"
                      style={{ paddingLeft: 0, marginTop: 12 }}
                      href={formaPagamentoInfo.invoice_url_pendente}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver fatura pendente atual
                    </Button>
                  )}
                </>
              )}
            </Modal>
            <Modal
              title="Atualizar cartão"
              open={modalCartaoOpen}
              onCancel={() => { setModalCartaoOpen(false); formCartao.resetFields(); }}
              footer={null}
              destroyOnClose
            >
              <Form form={formCartao} layout="vertical" onFinish={handleSubmitAtualizarCartao}>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Validação com seu banco"
                  description="Para confirmar o cartão, faremos uma validação temporária com o emissor. Você pode receber uma notificação no celular; o valor não será cobrado (a reserva é estornada em seguida). Isso pode levar até um minuto."
                />
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Dados do cartão (não armazenamos o número completo)
                </Text>
                <Form.Item
                  name="number"
                  label="Número do cartão"
                  validateTrigger="onBlur"
                  normalize={(value) => {
                    const digits = String(value || '').replace(/\D/g, '');
                    const isAmex = digits.startsWith('34') || digits.startsWith('37');
                    const maxDigits = isAmex ? 15 : 19;
                    return formatCardNumberDisplay(digits.slice(0, maxDigits));
                  }}
                  rules={[
                    { required: true, message: 'Informe o número do cartão' },
                    {
                      validator: (_, value) => {
                        const digits = String(value || '').replace(/\s/g, '');
                        if (!/^\d+$/.test(digits)) return Promise.reject(new Error('Use apenas números'));
                        const lengths = getAcceptedLengths(digits);
                        if (!lengths.includes(digits.length)) {
                          const brand = getCardBrand(digits);
                          const msg = brand ? `Número inválido para ${getCardBrandLabel(brand)}` : 'Número com quantidade inválida de dígitos';
                          return Promise.reject(new Error(msg));
                        }
                        if (!luhnCheck(digits)) return Promise.reject(new Error('Número do cartão inválido'));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="0000 0000 0000 0000" maxLength={23} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.number !== curr.number}>
                  {() => {
                    const num = formCartao.getFieldValue('number');
                    const brand = num ? getCardBrand(num) : null;
                    return brand ? <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -8, marginBottom: 8 }}>Bandeira: {getCardBrandLabel(brand)}</Text> : null;
                  }}
                </Form.Item>
                <Form.Item name="holderName" label="Nome no cartão" rules={[{ required: true, message: 'Informe o nome' }]}>
                  <Input placeholder="Como está no cartão" />
                </Form.Item>
                <Space style={{ width: '100%' }} size="middle">
                  <Form.Item
                    name="expiryMonth"
                    label="Mês"
                    rules={[
                      { required: true, message: 'Informe o mês' },
                      {
                        pattern: /^(0?[1-9]|1[0-2])$/,
                        message: 'Mês deve ser entre 01 e 12',
                        transform: (v) => (v ? String(v).replace(/\D/g, '') : v),
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="MM" maxLength={2} />
                  </Form.Item>
                  <Form.Item
                    name="expiryYear"
                    label="Ano"
                    dependencies={['expiryMonth']}
                    rules={[
                      { required: true, message: 'Informe o ano' },
                      {
                        validator: (_, value) => {
                          const year = String(value || '').replace(/\D/g, '');
                          if (year.length !== 2 && year.length !== 4) return Promise.reject(new Error('Ano deve ter 2 ou 4 dígitos'));
                          const month = formCartao.getFieldValue('expiryMonth');
                          if (!isExpiryFuture(month, year)) return Promise.reject(new Error('A validade deve ser uma data futura'));
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="AAAA" maxLength={4} />
                  </Form.Item>
                  <Form.Item
                    name="ccv"
                    label="CVV"
                    dependencies={['number']}
                    rules={[
                      { required: true, message: 'Informe o CVV' },
                      {
                        validator: (_, value) => {
                          const v = String(value || '').replace(/\D/g, '');
                          const cardNumber = formCartao.getFieldValue('number') || '';
                          const expectedLen = getCvvLength(cardNumber);
                          if (v.length !== expectedLen) {
                            return Promise.reject(new Error(expectedLen === 4 ? 'CVV deve ter 4 dígitos (American Express)' : 'CVV deve ter 3 dígitos'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="123" maxLength={4} type="password" autoComplete="off" />
                  </Form.Item>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                  Dados do titular
                </Text>
                <Form.Item name="name" label="Nome completo" rules={[{ required: true, message: 'Informe o nome' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="email" label="E-mail" rules={[{ required: true, type: 'email', message: 'E-mail válido' }]}>
                  <Input type="email" />
                </Form.Item>
                <Form.Item
                  name="cpfCnpj"
                  label="CPF/CNPJ"
                  rules={[
                    { required: true, message: 'Informe CPF (11 dígitos) ou CNPJ (14 dígitos)' },
                    {
                      validator: (_, value) => {
                        const digits = String(value || '').replace(/\D/g, '');
                        if (digits.length !== 11 && digits.length !== 14) {
                          return Promise.reject(new Error('CPF deve ter 11 dígitos ou CNPJ 14 dígitos'));
                        }
                        if (!isValidCpfOrCnpj(value)) return Promise.reject(new Error('CPF ou CNPJ inválido'));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Apenas números" maxLength={18} />
                </Form.Item>
                <Form.Item name="postalCode" label="CEP">
                  <Input placeholder="00000-000" maxLength={9} />
                </Form.Item>
                <Form.Item name="addressNumber" label="Número do endereço">
                  <Input />
                </Form.Item>
                <Form.Item name="phone" label="Telefone">
                  <Input placeholder="Apenas números" maxLength={15} />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
                  <Space>
                    <Button onClick={() => { setModalCartaoOpen(false); formCartao.resetFields(); }}>Cancelar</Button>
                    <Button type="primary" htmlType="submit" loading={actionLoading === 'cartao'}>
                      Atualizar cartão
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>
          </>
        )}
      </Card>
      {faturas.length > 0 && (
        <Card title="Histórico de cobranças" style={{ marginTop: 24 }}>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={faturas}
            columns={[
              {
                title: 'Vencimento',
                dataIndex: 'vencimento',
                render: (v) => formatarData(v) || v,
              },
              { title: 'Descrição', dataIndex: 'descricao' },
              { title: 'Valor', dataIndex: 'valor', render: (v) => formatCurrency(v) },
              {
                title: 'Status',
                dataIndex: 'status_label',
                render: (label, row) => label || row.status,
              },
              {
                title: '',
                key: 'link',
                render: (_, row) =>
                  row.invoice_url ? (
                    <Button type="link" size="small" href={row.invoice_url} target="_blank" rel="noopener noreferrer">
                      Ver fatura
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}
    </Content>
  );
};

export default MeuPlanoPage;
